import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Button } from '@/components/ui/Button';
import { SystemSettings } from '@/hooks/common';
import { useEmailTest } from '@/hooks/email';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';

interface SmtpSettingsProps {
  settings: SystemSettings;
  onSettingsChange: (settings: Partial<SystemSettings>) => void;
  onSave: () => Promise<string>;
  loading: boolean;
}

export const SmtpSettings: React.FC<SmtpSettingsProps> = ({
  settings,
  onSettingsChange,
  onSave,
  loading,
}) => {
  const [testEmailRecipient, setTestEmailRecipient] = useState('');
  const { testing, testResult, setTestResult, testEmail } = useEmailTest();

  const handleSave = async () => {
    try {
      await onSave();
    } catch (error) {
      // Error is handled by parent
    }
  };

  const handleTestEmail = async () => {
    const success = await testEmail(testEmailRecipient);
    if (success) {
      setTestEmailRecipient('');
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Gmail SMTP 설정</CardTitle>
          <CardDescription>이메일 발송을 위한 Gmail 설정을 입력합니다</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              💡 SMTP 설정은 .env 파일에서 자동으로 불러와집니다. 필요시에만 수정하세요.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="smtp_host">SMTP 호스트</Label>
                <Input
                  id="smtp_host"
                  value={settings.smtpHost}
                  onChange={(e) => onSettingsChange({ smtpHost: e.target.value })}
                  placeholder="smtp.gmail.com"
                  className="bg-gray-50 text-gray-700"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp_port">SMTP 포트</Label>
                <Input
                  id="smtp_port"
                  type="number"
                  value={settings.smtpPort}
                  onChange={(e) => onSettingsChange({ smtpPort: e.target.value })}
                  placeholder="587"
                  className="bg-gray-50 text-gray-700"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp_user">SMTP 사용자 이메일</Label>
                <Input
                  id="smtp_user"
                  type="email"
                  value={settings.smtpUser}
                  onChange={(e) => onSettingsChange({ smtpUser: e.target.value })}
                  placeholder="your-email@gmail.com"
                  className="bg-gray-50 text-gray-700"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp_password">SMTP 비밀번호 (앱 비밀번호)</Label>
                <Input
                  id="smtp_password"
                  type="password"
                  value={settings.smtpPassword}
                  onChange={(e) => onSettingsChange({ smtpPassword: e.target.value })}
                  placeholder="16자리 앱 비밀번호"
                  className="bg-gray-50 text-gray-700"
                />
                <p className="text-xs text-gray-500">
                  Gmail 앱 비밀번호를 사용해야 합니다. Google 계정 설정에서 생성하세요.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp_from_email">발신자 이메일 주소</Label>
                <Input
                  id="smtp_from_email"
                  type="email"
                  value={settings.smtpFromEmail}
                  onChange={(e) => onSettingsChange({ smtpFromEmail: e.target.value })}
                  placeholder="your-email@gmail.com"
                  className="bg-gray-50 text-gray-700"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp_from_name">발신자 이름</Label>
                <Input
                  id="smtp_from_name"
                  value={settings.smtpFromName}
                  onChange={(e) => onSettingsChange({ smtpFromName: e.target.value })}
                  placeholder="젠아이랩스 Auth Server"
                  className="bg-gray-50 text-gray-700"
                />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button onClick={handleSave} disabled={loading}>
                {loading ? '저장 중...' : '저장'}
              </Button>
              <div className="flex items-center gap-2 flex-1 max-w-md">
                <Input
                  type="email"
                  value={testEmailRecipient}
                  onChange={(e) => setTestEmailRecipient(e.target.value)}
                  placeholder="테스트 메일 수신자 이메일"
                  className="flex-1"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleTestEmail();
                    }
                  }}
                />
                <Button
                  onClick={handleTestEmail}
                  disabled={testing || loading}
                  variant="outline"
                >
                  {testing ? '전송 중...' : '메일 보내기 테스트'}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={testResult.isOpen}>
        <DialogContent onClose={() => setTestResult({ ...testResult, isOpen: false })}>
          <DialogHeader>
            <DialogTitle className={testResult.success ? 'text-green-600' : 'text-red-600'}>
              {testResult.success ? '✅ 테스트 메일 전송 성공' : '❌ 테스트 메일 전송 실패'}
            </DialogTitle>
            <DialogDescription>
              {testResult.message}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setTestResult({ ...testResult, isOpen: false })}>
              확인
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

